/**
 * Thin fetch wrapper for the Django API.
 *
 * - Same-origin session cookies; CSRF token read from the `learn_csrftoken` cookie (CSRF_COOKIE_NAME)
 *   and sent as X-CSRFToken on unsafe methods.
 * - Non-2xx responses throw ApiError carrying the server's { detail, code, fields }.
 */
import type { ApiErrorBody } from "./types";

export const API_BASE = "/api";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string[]>;

  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.detail || `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code || "error";
    this.fields = body.fields ?? {};
  }

  /** First message for a form field, if the server rejected it. */
  fieldError(field: string): string | undefined {
    return this.fields[field]?.[0];
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Human-readable message for any thrown value (for toasts and error states). */
export function errorMessage(error: unknown, fallback = "Something went wrong. Please try again."): string {
  if (isApiError(error)) return error.message;
  if (error instanceof TypeError) return "Can't reach the server. Check your connection and try again.";
  return fallback;
}

export function getCookie(name: string): string | null {
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
  /** Keep the request alive while the page unloads (telemetry). */
  keepalive?: boolean;
}

export async function request<T>(
  path: string,
  { method = "GET", body, signal, keepalive }: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  // FormData (file uploads) sets its own multipart Content-Type with the boundary.
  const multipart = body instanceof FormData;
  if (body !== undefined && !multipart) headers["Content-Type"] = "application/json";
  if (method !== "GET") {
    const token = getCookie("learn_csrftoken");
    if (token) headers["X-CSRFToken"] = token;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : multipart ? body : JSON.stringify(body),
    credentials: "same-origin",
    signal,
    keepalive,
  });

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw new ApiError(response.status, (isJson ? payload : { detail: String(payload) }) as Partial<ApiErrorBody>);
  }
  return payload as T;
}

export const http = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "POST", body: body ?? {} }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T = void>(path: string) => request<T>(path, { method: "DELETE" }),
};

/** Build a query string, skipping empty values. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}
